/**
 * V13.33 — Formulaire général d'exercice des droits RGPD.
 *
 * Accessible depuis /legal/contact-droits (surchargé quand la page est en
 * `status=published` : sinon la LegalPage classique s'affiche). Le formulaire
 * envoie un POST vers /api/rgpd/general — jamais de donnée personnelle en URL.
 */
import { useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { toast } from "sonner";
import { CheckCircle2, Mail, Scale } from "lucide-react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { CollectNotice } from "@/components/CollectNotice";

const REQUEST_TYPES = [
  { value: "access",             label: "Accès à mes données" },
  { value: "rectification",      label: "Rectification" },
  { value: "erasure",            label: "Effacement" },
  { value: "restriction",        label: "Limitation" },
  { value: "opposition",         label: "Opposition" },
  { value: "consent_withdrawal", label: "Retrait du consentement" },
  { value: "portability",        label: "Portabilité" },
  { value: "minor",              label: "Demande concernant un mineur" },
  { value: "other",              label: "Autre demande RGPD" },
];

export function RgpdContactForm() {
  const [type, setType] = useState("access");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [url, setUrl] = useState("");
  const [description, setDescription] = useState("");
  const [role, setRole] = useState("self");
  const [loading, setLoading] = useState(false);
  const [confirmation, setConfirmation] = useState(null);

  const submit = async () => {
    if (!name.trim() || name.trim().length < 2) return toast.error("Ton nom complet est requis.");
    if (!email.trim() || !email.includes("@")) return toast.error("Une adresse email valide est requise.");
    if (description.trim().length < 10) return toast.error("Merci de décrire ta demande (10 caractères minimum).");
    setLoading(true);
    try {
      const { data } = await api.post("/rgpd/general", {
        request_type: type,
        contact_name: name.trim(),
        contact_email: email.trim(),
        concerned_url: url.trim() || null,
        description: description.trim(),
        requester_role: role,
      });
      setConfirmation(data);
      toast.success(`Demande enregistrée. Numéro de suivi : ${data.tracking_id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Envoi impossible. Réessaie plus tard.");
    } finally { setLoading(false); }
  };

  if (confirmation) {
    return (
      <Card className="p-6 md:p-8 border-emerald-300 bg-emerald-50/50 dark:bg-emerald-950/20" data-testid="rgpd-confirmation">
        <div className="flex items-start gap-3">
          <CheckCircle2 className="h-7 w-7 text-emerald-700 shrink-0 mt-0.5" aria-hidden="true" />
          <div>
            <h2 className="font-display text-xl font-semibold">Demande enregistrée</h2>
            <p className="text-sm text-muted-foreground mt-1 leading-relaxed">
              Numéro de suivi : <span className="font-mono font-semibold text-foreground">{confirmation.tracking_id}</span>
              <br />
              Conserve ce numéro pour toute reprise de contact. Nous te répondrons sous un mois maximum.
            </p>
            <div className="mt-4 flex gap-2">
              <Link to="/legal"><Button size="sm" variant="outline">Retour</Button></Link>
              <Button size="sm" onClick={() => { setConfirmation(null); setDescription(""); setUrl(""); }}>Nouvelle demande</Button>
            </div>
          </div>
        </div>
      </Card>
    );
  }

  return (
    <Card className="p-6 md:p-8" data-testid="rgpd-general-form">
      <div className="flex items-start gap-3 mb-5">
        <Mail className="h-5 w-5 text-[hsl(var(--aito-lagoon))] mt-1 shrink-0" aria-hidden="true" />
        <div>
          <h2 className="font-display text-xl font-semibold">Formulaire d'exercice des droits</h2>
          <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
            Aucune donnée n'est transmise en clair dans l'URL. Nous te répondons sous un mois maximum.
            Une pièce d'identité pourra t'être demandée ultérieurement uniquement si nécessaire.
          </p>
        </div>
      </div>

      <div className="space-y-4">
        <CollectNotice variant="rights" className="rounded-md bg-muted/40 p-3" testId="rgpd-collect-notice" />
        <div>
          <Label htmlFor="rgpd-type">Type de demande *</Label>
          <Select value={type} onValueChange={setType}>
            <SelectTrigger id="rgpd-type" data-testid="rgpd-type-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {REQUEST_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        <div className="grid sm:grid-cols-2 gap-3">
          <div>
            <Label htmlFor="rgpd-name">Nom complet *</Label>
            <Input id="rgpd-name" value={name} onChange={(e) => setName(e.target.value)} data-testid="rgpd-name-input" />
          </div>
          <div>
            <Label htmlFor="rgpd-email">Adresse électronique *</Label>
            <Input id="rgpd-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="rgpd-email-input" />
          </div>
        </div>

        <div>
          <Label htmlFor="rgpd-url">URL de la fiche ou page concernée (optionnel)</Label>
          <Input id="rgpd-url" placeholder="https://aito.pf/athletes/…" value={url} onChange={(e) => setUrl(e.target.value)} data-testid="rgpd-url-input" />
        </div>

        <div>
          <Label>Qualité du demandeur *</Label>
          <RadioGroup value={role} onValueChange={setRole} className="flex flex-wrap gap-3 mt-1.5">
            <label className="flex items-center gap-1.5 text-sm cursor-pointer">
              <RadioGroupItem value="self" data-testid="rgpd-role-self" /> Personne concernée
            </label>
            <label className="flex items-center gap-1.5 text-sm cursor-pointer">
              <RadioGroupItem value="legal_representative" data-testid="rgpd-role-legal" /> Représentant légal
            </label>
            <label className="flex items-center gap-1.5 text-sm cursor-pointer">
              <RadioGroupItem value="other" data-testid="rgpd-role-other" /> Autre
            </label>
          </RadioGroup>
        </div>

        <div>
          <Label htmlFor="rgpd-desc">Description de la demande *</Label>
          <Textarea id="rgpd-desc" rows={5} value={description} onChange={(e) => setDescription(e.target.value)} data-testid="rgpd-desc-input" />
        </div>

        <div className="flex items-center justify-between pt-2 border-t">
          <Link to="/legal" className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1">
            <Scale className="h-3 w-3" /> Retour à l'espace juridique
          </Link>
          <Button onClick={submit} disabled={loading} data-testid="rgpd-submit"
                  className="bg-[hsl(var(--aito-lagoon))] text-black hover:bg-[hsl(var(--aito-lagoon-2))]">
            {loading ? "Envoi…" : "Envoyer ma demande"}
          </Button>
        </div>
      </div>
    </Card>
  );
}
